/** Parameter labels for Cross-selling (Schneider) comparison table */
export const MAX_COMPARISON_CUSTOM_COLUMNS = 9
/** Products compared — horizontal columns in the form (API `rows`) */
export const MAX_COMPARISON_CUSTOM_ROWS = 10

export interface IComparisonCustomRow {
  name: string
  paramValues: string[]
  link: string
  order?: number | null
  imageUrl?: string
  imageBucketKey?: string
  imageFileName?: string
}

export interface IComparisonCustom {
  paramNames: string[]
  rows: IComparisonCustomRow[]
}

export const getDefaultComparisonCustom = (): IComparisonCustom => ({
  paramNames: [''],
  rows: Array.from({ length: MAX_COMPARISON_CUSTOM_ROWS }, (_, i) => ({
    name: '',
    paramValues: [''],
    link: '',
    order: i
  }))
})

/** Row has any user-visible content (empty slots are omitted on save). */
export function comparisonCustomRowHasContent(
  row: IComparisonCustomRow | undefined
): boolean {
  if (!row) return false
  const name = String(row.name ?? '').trim()
  const link = String(row.link ?? '').trim()
  const cells = (row.paramValues ?? []).some(v => String(v ?? '').trim())
  const img =
    String(row.imageUrl ?? '').trim() ||
    String(row.imageBucketKey ?? '').trim() ||
    String(row.imageFileName ?? '').trim()

  return Boolean(name || link || cells || img)
}

/** Align column counts when loading from API; pad/truncate to MAX_COMPARISON_CUSTOM_ROWS for the form grid. */
export function normalizeComparisonCustom(cc: IComparisonCustom): IComparisonCustom {
  const paramNames =
    cc.paramNames?.length > 0
      ? cc.paramNames.map(n => String(n ?? ''))
      : ['']
  const colCount = paramNames.length
  const sourceRows =
    cc.rows?.length > 0
      ? cc.rows
      : [
          {
            name: '',
            paramValues: [],
            link: '',
            order: 0
          }
        ]
  let rows = sourceRows.map((row, idx) => {
    const pv = [...(row.paramValues ?? [])]

    while (pv.length < colCount) pv.push('')
    pv.length = colCount

    return {
      ...row,
      name: String(row.name ?? ''),
      link: String(row.link ?? ''),
      paramValues: pv,
      order: row.order ?? idx
    }
  })

  if (rows.length > MAX_COMPARISON_CUSTOM_ROWS) {
    rows = rows.slice(0, MAX_COMPARISON_CUSTOM_ROWS)
  }

  while (rows.length < MAX_COMPARISON_CUSTOM_ROWS) {
    rows.push({
      name: '',
      link: '',
      paramValues: Array(colCount).fill(''),
      order: rows.length
    })
  }

  return { paramNames, rows }
}

/**
 * Drops trailing property columns where header and all cells are blank (not sent to API).
 */
export function stripTrailingEmptyComparisonCustomColumns(
  cc: IComparisonCustom
): IComparisonCustom {
  let paramNames = [...(cc.paramNames ?? [])]
  let rows = cc.rows.map(r => ({
    ...r,
    paramValues: [...(r.paramValues ?? [])]
  }))

  if (paramNames.length <= 1) return { ...cc, paramNames, rows }

  while (paramNames.length > 1) {
    const last = paramNames.length - 1
    const headerEmpty = !String(paramNames[last] ?? '').trim()
    const colEmpty = rows.every(
      r => !String(r.paramValues?.[last] ?? '').trim()
    )

    if (!headerEmpty || !colEmpty) break
    paramNames = paramNames.slice(0, -1)
    rows = rows.map(r => ({
      ...r,
      paramValues: (r.paramValues || []).slice(0, -1)
    }))
  }

  return { ...cc, paramNames, rows }
}

export function isComparisonCustomEmpty(
  cc: IComparisonCustom | null | undefined,
  rowFiles?: (File | null | undefined)[] | null
): boolean {
  if (!cc?.paramNames?.length || !cc?.rows?.length) return true
  const headers = cc.paramNames.map(s => String(s ?? '').trim()).filter(Boolean)

  if (headers.length === 0) return true
  const hasRowContent = cc.rows.some(
    (row, i) =>
      comparisonCustomRowHasContent(row) || Boolean(rowFiles?.[i])
  )

  return !hasRowContent
}

/** Returns error message or null when valid */
export function validateComparisonCustom(
  cc: IComparisonCustom | null | undefined,
  rowFiles?: (File | null | undefined)[] | null
): string | null {
  if (!cc || isComparisonCustomEmpty(cc, rowFiles)) return null
  const headers = cc.paramNames.map(s => String(s ?? '').trim())
  const filledHeaders = headers.filter(Boolean)

  if (filledHeaders.length === 0) {
    return 'Cross-selling: add at least one column header.'
  }

  if (filledHeaders.length > MAX_COMPARISON_CUSTOM_COLUMNS) {
    return `Cross-selling: at most ${MAX_COMPARISON_CUSTOM_COLUMNS} column headers.`
  }

  if (headers.length !== filledHeaders.length) {
    return 'Cross-selling: fill all column headers or remove empty columns.'
  }

  const colCount = headers.length

  for (let i = 0; i < cc.rows.length; i++) {
    const row = cc.rows[i]
    const active =
      comparisonCustomRowHasContent(row) || Boolean(rowFiles?.[i])

    if (!active) continue
    if (!String(row?.name ?? '').trim()) {
      return `Cross-selling: row ${i + 1} needs a name.`
    }

    const pv = row.paramValues ?? []

    if (pv.length !== colCount) {
      return 'Cross-selling: each row must have one value per column.'
    }

    for (let j = 0; j < colCount; j++) {
      if (!String(pv[j] ?? '').trim()) {
        return 'Cross-selling: fill all table cells.'
      }
    }
  }

  return null
}

/** Non-empty rows only (API payload); preserves original row indices for image file mapping. */
export function stripEmptyComparisonCustomRows(
  cc: IComparisonCustom,
  rowFiles?: (File | null | undefined)[] | null
): { cc: IComparisonCustom; keptOriginalRowIndices: number[] } {
  const keptOriginalRowIndices: number[] = []
  const keptRows: IComparisonCustomRow[] = []

  cc.rows.forEach((row, origIdx) => {
    const keep =
      comparisonCustomRowHasContent(row) || Boolean(rowFiles?.[origIdx])

    if (keep) {
      keptOriginalRowIndices.push(origIdx)
      keptRows.push({ ...row, order: keptRows.length })
    }
  })

  return {
    cc: { ...cc, rows: keptRows },
    keptOriginalRowIndices
  }
}

export function stripComparisonCustomRowImagesForUpload(
  cc: IComparisonCustom,
  rowFiles: (File | null | undefined)[] | undefined
): IComparisonCustom {
  if (!rowFiles?.length) return cc
  const rows = cc.rows.map((row, i) => {
    if (!rowFiles[i]) return row
    const next = { ...row }

    delete next.imageUrl
    delete next.imageBucketKey
    delete next.imageFileName

    return next
  })

  return { ...cc, rows }
}
